import { registerNavigationScreenContext, setUTMParams } from 'features/navigation/utils/analytics';
import { getCrashlytics, setAttribute } from 'features/observability/crashlytics';
import { addContext as addLoggerContext } from 'features/observability/datadog';
import logger from 'features/observability/logger';
import { Platform } from 'react-native';

import { getStaticPathAndSearch } from 'navigation/location/location';
import { extractQueryParams } from 'navigation/utils';
import { Header, setHeaders } from 'state/graphql/headers';
import { appVersion } from 'utils/app-version-info';
import { getDeviceIdAsync } from 'utils/device-id';
import { getApiKey } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

import { getAmplitudeSessionId, setAmplitudeDeviceId } from './providers/amplitude/client';
import { initializeAmplitudeProvider } from './providers/amplitude/initialize';

import { updateUniversalAttributes } from './index';

const amplitudeKey = getApiKey({ key: 'amplitude' });
const staticAmplitudeSessionId = { current: 'UN-INITIALIZED' };
let amplitudeInitializationPromise: Promise<void> | null = null;

export const initializeAmplitude = (
  refreshAuthenticatedUserAttributes: () => void,
  refreshAdvertisingId: () => Promise<void>
): Promise<void> => {
  if (!amplitudeInitializationPromise) {
    amplitudeInitializationPromise = initializeAmplitudeClient(
      refreshAuthenticatedUserAttributes,
      refreshAdvertisingId
    );
  }

  return amplitudeInitializationPromise;
};

/**
 * Initialize the Amplitude client.
 * If a user is already logged in, we will initialize with the user's cognito ID.
 */
async function initializeAmplitudeClient(
  refreshAuthenticatedUserAttributes: () => void,
  refreshAdvertisingId: () => Promise<void>
) {
  const trace = startTrace(TraceName.APP_START_INIT_AMPLITUDE);

  try {
    registerNavigationScreenContext();
    const cognitoId = syncStorage.getString(SyncStorageKey.USER_ID);
    updateUniversalAttributes({ isLoyaltyUser: !!cognitoId });

    const options = {
      trackingSessionEvents: true,
      appVersion,
      autocapture: {
        sessions: true,
        pageViews: false,
        attribution: false,
      },
      trackingOptions: {
        appSetId: true,
        idfv: true,
      },
    };

    await initializeAmplitudeProvider(amplitudeKey ?? '', cognitoId || undefined, options);
    if (Platform.OS === 'web') {
      setUTMParams(extractQueryParams(getStaticPathAndSearch()));
    }
    if (cognitoId) {
      refreshAuthenticatedUserAttributes();
    }

    const amplitudeSessionId = getAmplitudeSessionId()?.toString() ?? '';

    addLoggerContext('amplitudeSession', amplitudeSessionId);
    setAttribute(getCrashlytics(), 'amplitudeSession', amplitudeSessionId);
    setHeaders(Header.SESSION_ID, amplitudeSessionId);
    staticAmplitudeSessionId.current = amplitudeSessionId;

    setAmplitudeDeviceId(await getDeviceIdAsync());

    await refreshAdvertisingId();
  } catch (error) {
    // this is temporary until we remove this code from the backend as this timestamp amp session id
    // is being used for security checks
    setHeaders(Header.SESSION_ID, new Date().getTime().toString());
    logger.error({ error, message: 'Unknown amplitude initialization error' });
  } finally {
    trace?.stop();
  }
}
